import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

import { C_value, Store, User } from '@app/shared/_models/common';
import { GeoService } from '@app/shared/geo.service';
import { DeliveryPlanning } from '../../logistic.service';
import { DelRun, DelrunsService, RouteVM } from '../delruns.service';
import { minutesFromMidnight } from '@app/shared/time';
import { ScheduleResult, dayOffsetOf, describeIssue, toHhmm } from '../delrun-schedule';
import { DeliveryCardComponent } from '../delivery-card/delivery-card.component';

// Ce qu'on montre pour l'heure d'arrivée d'un arrêt — et surtout : à quel titre.
//   live      : calcul frais         => « ≈ 10:47 »
//   locked    : contrainte posée     => « 10:47 » épinglé, trajet entrant barré
//   committed : engagement enregistré, recalcul indisponible => « 10:47 » daté, jamais confondu avec un calcul
//   unknown   : rien à afficher      => « — »
// L'utilisateur doit toujours savoir laquelle des trois il regarde : c'est tout l'objet de cet état.
export type ArrivalState = 'live' | 'locked' | 'committed' | 'unknown';

// Le trajet qu'impose une heure fixée, montré en face du trajet réel barré : « ce que la contrainte
// laisse » contre « ce que ça prend ». Négatif = l'heure posée précède la fin de la livraison précédente.
export interface ImpliedLegView {
	label: string;				// « 30 min », « −15 min »
	negative: boolean;
	title: string;
}

export interface ArrivalView {
	state: ArrivalState;
	hhmm: string;					// valeur du <input type="time"> ('' si inconnue)
	dayOffset: number;		// 1 = arrivée le lendemain (tournée qui déborde après minuit)
	issues: string[];			// diagnostics lisibles (contrainte intenable, demande client ratée…)
	title: string;				// infobulle expliquant l'état
	implied: ImpliedLegView | null;		// trajet imposé — seul l'état « locked » en a un
}

// UNE tournée (colonne du board) : véhicule + livreurs + livraisons ordonnées (drag & drop).
// Le composant ne persiste rien : tout remonte au parent (dropped / changed / save / remove),
// qui possède les tableaux de livraisons (cdkDropListData) et poste les events.
@Component({
	selector: 'app-delrun',
	imports: [ReactiveFormsModule, DragDropModule, MatButtonModule, MatButtonToggleModule, MatFormFieldModule, MatIconModule, MatSelectModule, MatCheckboxModule, DeliveryCardComponent],
	templateUrl: './delrun.component.html',
	styleUrl: './delrun.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: true,
})
export class DelrunComponent implements OnInit, OnChanges, OnDestroy {

	@Input({ required: true }) run!: DelRun;
	@Input({ required: true }) deliveries!: DeliveryPlanning[];		// référence partagée avec le parent (drag & drop)
	@Input() vehicles: C_value[] = [];	// c_values C_VALUES_VEHICLE
	@Input() users: User[] = [];				// tous les utilisateurs (TODO hr_worker)
	@Input() deliverers: User[] = [];		// utilisateurs de rôle Livreur — liste par défaut du select
	@Input() slots: C_value[] = [];
	@Input() ops: C_value[] = [];
	@Input() dirty = false;						// modifications non enregistrées (géré par le parent)
	@Input() saving = false;					// POST Valider en cours (géré par le parent) : boutons désactivés
	@Input() color = '#1976d2';
	@Input() stores: Store[] = [];							// établissements — dépôt de départ
	@Input() depotStore: number | null = null;	// établissement de départ courant (géré par le parent)
	@Input() startSlot: number | null = null;		// heure de départ (créneau horaire c_values 8, géré par le parent)
	@Input() route: RouteVM | null = null;			// temps de trajet calculés par le parent (Géoplateforme)
	@Input() schedule: ScheduleResult | null = null;	// heures d'arrivée + diagnostics calculés par le parent
	@Input() highlightBl: number | null = null;		// BL en surbrillance (?bl= du board), transmis à sa vignette

	@Output() dropped = new EventEmitter<CdkDragDrop<DeliveryPlanning[]>>();
	@Output() changed = new EventEmitter<void>();		// véhicule / livreurs modifiés
	@Output() save = new EventEmitter<void>();			// « Valider la tournée »
	@Output() remove = new EventEmitter<void>();
	@Output() printBl = new EventEmitter<void>();		// imprimante : tous les BL de la tournée en un PDF (le parent appelle le serveur)
	@Output() depotChange = new EventEmitter<number | null>();
	@Output() startSlotChange = new EventEmitter<number | null>();
	@Output() slotChange = new EventEmitter<number | null>();		// créneau de la tournée (toggle 3 états)
	@Output() durationChange = new EventEmitter<void>();		// temps sur place modifié sur une carte
	@Output() arrivalChange = new EventEmitter<{ index: number; value: string }>();		// heure d'arrivée fixée (« HH:mm »)
	@Output() arrivalClear = new EventEmitter<number>();		// croix « délier » : retour au calcul automatique

	private fb = inject(FormBuilder);
	private formSubscription!: Subscription;

	public formGroup = this.fb.group({
		vehicle: [null as number | null],
		workers: [[] as number[]],
	});

	// Livreurs proposés : par défaut les utilisateurs de rôle Livreur ; « Tous les utilisateurs » permet
	// d'ajouter exceptionnellement quelqu'un d'autre. Les personnes déjà affectées restent toujours visibles.
	public showAllWorkers = false;

	get workerOptions(): User[] {
		const base = this.showAllWorkers ? this.users : this.deliverers;
		const selected = this.run?.workers ?? [];
		const extra = this.users.filter((u) => u.id != null && selected.includes(u.id) && !base.some((b) => b.id === u.id));
		return [...base, ...extra];
	}

	ngOnInit(): void {
		// Toute modification du formulaire est répercutée sur la tournée et signalée au parent (dirty)
		this.formSubscription = this.formGroup.valueChanges.subscribe((v) => {
			this.run.vehicle = v.vehicle ?? null;
			this.run.workers = v.workers ?? [];
			this.changed.emit();
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['run'] && this.run) {
			this.formGroup.patchValue({ vehicle: this.run.vehicle ?? null, workers: this.run.workers ?? [] }, { emitEvent: false });
		}
	}

	ngOnDestroy(): void {
		this.formSubscription?.unsubscribe();
	}

	onDrop(event: CdkDragDrop<DeliveryPlanning[]>): void {
		this.dropped.emit(event);
	}

	// Tournée enregistrée, identique à la base et non vide : sinon le PDF ne serait pas ce que montre la colonne.
	get canPrint(): boolean {
		return !!this.run.id && !this.dirty && !this.saving && this.deliveries.length > 0;
	}

	// disabledInteractive laisse passer le clic d'un bouton grisé : la garde est ici.
	onPrint(): void {
		if (this.canPrint) this.printBl.emit();
	}

	userLabel(u: User): string {
		return ((u?.firstname ?? '') + ' ' + (u?.lastname ?? '')).trim() || (u?.login ?? String(u?.id ?? ''));
	}

	// Toggle du créneau (même logique que le filtre de l'entête de page) : re-clic sur le créneau
	// déjà actif => Journée (null) ; sinon => le créneau cliqué. Remonte au parent.
	onSlotToggle(day: number): void {
		const next = (this.run?.slot ?? null) === day ? null : day;
		this.slotChange.emit(next);
	}

	// Heure de départ : créneaux horaires filtrés selon le créneau de la tournée (Matin => matin,
	// Après-midi => après-midi, Journée => tous) — logique mutualisée dans DelrunsService.
	get hourSlots(): C_value[] {
		return DelrunsService.hourSlotsFor(this.slots, this.run?.slot ?? null);
	}

	// --- Heures d'arrivée (calculées par le parent, simple affichage ici) ---

	// Ce qu'on affiche pour l'arrêt i, et à quel titre. Règle : le calcul frais prime ; l'engagement
	// enregistré (plandatdel) ne sert que de repli EXPLICITE quand le calcul n'est pas disponible
	// (itinéraire en cours, coordonnées manquantes, pas d'heure de départ). Il n'est jamais mélangé
	// en silence à des trajets fraîchement calculés : c'est ce mensonge-là qu'on veut éviter.
	arrivalView(i: number): ArrivalView {
		const stop = this.schedule?.stops?.[i];
		const d = this.deliveries?.[i];
		const issues = (stop?.issues ?? []).map(describeIssue);
		const pending = !!this.route?.pending;

		if (stop && stop.locked) {
			return { state: 'locked', hhmm: toHhmm(stop.arrivalMin), dayOffset: dayOffsetOf(stop.arrivalMin), issues,
				implied: DelrunComponent.impliedLegView(stop.impliedLegMin),
				title: this.titleOf('Heure fixée à la main : les arrêts suivants se calent dessus', issues) };
		}
		if (!pending && stop && stop.arrivalMin !== null) {
			const natural = 'Heure estimée (Géoplateforme IGN) — modifiez-la pour la fixer';
			return { state: 'live', hhmm: toHhmm(stop.arrivalMin), dayOffset: dayOffsetOf(stop.arrivalMin), issues, implied: null,
				title: this.titleOf(natural, issues) };
		}
		// Calcul indisponible : on montre l'engagement pris à la dernière validation, en le disant.
		const committed = minutesFromMidnight(d?.plandatdel, this.run?.dat);
		if (committed !== null) {
			return { state: 'committed', hhmm: toHhmm(committed), dayOffset: dayOffsetOf(committed), issues, implied: null,
				title: this.titleOf(pending ? 'Heure enregistrée à la dernière validation — recalcul en cours…'
					: 'Heure enregistrée à la dernière validation — non recalculée (itinéraire indisponible)', issues) };
		}
		return { state: 'unknown', hhmm: '', dayOffset: 0, issues, implied: null,
			title: this.titleOf(pending ? 'Calcul de l\'itinéraire…' : 'Heure inconnue : ni itinéraire calculable, ni heure enregistrée', issues) };
	}

	// Positif : ce que l'heure fixée laisse pour rouler (à comparer au trajet barré, qui est ce qu'il
	// faudrait). Négatif : elle précède la fin de la livraison précédente.
	static impliedLegView(min: number | null | undefined): ImpliedLegView | null {
		if (min === null || min === undefined) return null;
		const negative = min < 0;
		const label = (negative ? '−' : '') + GeoService.formatDuration(Math.abs(min) * 60);
		return { label, negative, title: negative
			? `Heure fixée ${-min} min AVANT la fin de la livraison précédente : aucun trajet ne peut la tenir`
			: `Trajet que laisse l'heure fixée depuis le point précédent : ${label}` };
	}

	private titleOf(base: string, issues: string[]): string {
		return issues.length ? [base, ...issues].join('\n') : base;
	}

	// Saisie d'une heure d'arrivée sur la carte i => remonte au parent (pose la contrainte, ou délie si vidé)
	onArrivalInput(i: number, value: string): void {
		this.arrivalChange.emit({ index: i, value });
	}

	legLabel(i: number): string {		// durée du trajet VERS la livraison i (i = 0 : depuis le dépôt)
		if (!this.route || this.route.pending) return '…';
		const s = this.route.legs?.[i];
		return (s === null || s === undefined) ? '—' : GeoService.formatDuration(s);
	}

	get totalLabel(): string {
		if (!this.route) return '';
		if (this.route.pending) return 'calcul…';
		if (this.route.duration === null) return 'indisponible';
		const km = this.route.distance !== null ? ' · ' + (this.route.distance / 1000).toFixed(this.route.distance >= 9950 ? 0 : 1) + ' km' : '';
		return GeoService.formatDuration(this.route.duration) + km;
	}
}
